import { useState, useEffect, FormEvent } from 'react';
import {
  TextField, Button, Typography, Alert, CircularProgress,
  Box, InputAdornment, IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AuthLayout } from '@/components/AuthLayout';
import { getInvitationByToken, activateAccountDirect } from '@timetable/api';
import { validatePassword, getPasswordRequirementsText } from '@timetable/utils';

type PageState = 'loading' | 'form' | 'done' | 'invalid';

export function ActivateAccountPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [pageState, setPageState] = useState<PageState>('loading');
  const [inviteInfo, setInviteInfo] = useState<{
    email: string; role: string; name: string | null; schoolId: string;
  } | null>(null);
  const [invalidReason, setInvalidReason] = useState('');

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!token) { setInvalidReason('No invitation token found in the link.'); setPageState('invalid'); return; }

    getInvitationByToken(token).then((result) => {
      if (!result.valid) {
        setInvalidReason(result.error ?? 'Invalid invitation link.');
        setPageState('invalid');
      } else {
        setInviteInfo({ email: result.email, role: result.role, name: result.name, schoolId: result.schoolId });
        setName(result.name ?? '');
        setPageState('form');
      }
    });
  }, [token]);

  const validate = () => {
    const result = validatePassword(password, confirmPassword);
    if (!result.valid) {
      setPasswordError(result.errors.password || result.errors.confirmPassword || '');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await activateAccountDirect(token, password, name || undefined);
      setPageState('done');
    } catch (err: any) {
      setSubmitError(err.message ?? 'Activation failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (pageState === 'loading') {
    return (
      <AuthLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </AuthLayout>
    );
  }

  if (pageState === 'invalid') {
    return (
      <AuthLayout>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>Invalid Link</Typography>
        <Alert severity="error" sx={{ mb: 2 }}>{invalidReason}</Alert>
        <Typography variant="body2" color="text.secondary">
          Please contact your administrator for a new invitation.
        </Typography>
        <Button variant="outlined" sx={{ mt: 3 }} onClick={() => navigate('/login')}>
          Back to Login
        </Button>
      </AuthLayout>
    );
  }

  if (pageState === 'done') {
    return (
      <AuthLayout>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>Account Activated!</Typography>
        <Alert severity="success" sx={{ mb: 2 }}>
          Your account has been set up successfully. You can now sign in.
        </Alert>
        <Button variant="contained" fullWidth onClick={() => navigate('/login')}>
          Go to Login
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>Activate Your Account</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Set a password for <strong>{inviteInfo?.email}</strong>
      </Typography>

      {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}

      <form onSubmit={handleSubmit}>
        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Your Name</Typography>
        <TextField
          fullWidth size="small" value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your full name" autoFocus sx={{ mb: 2 }}
        />

        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Password</Typography>
        <TextField
          fullWidth size="small" type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => { setPassword(e.target.value); if (passwordError) setPasswordError(''); }}
          required placeholder="••••••••"
          helperText={getPasswordRequirementsText()}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowPassword((s) => !s)}>
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Confirm Password</Typography>
        <TextField
          fullWidth size="small" type={showPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => { setConfirmPassword(e.target.value); if (passwordError) setPasswordError(''); }}
          required placeholder="••••••••"
          error={!!passwordError} helperText={passwordError}
          sx={{ mb: 3 }}
        />

        <Button fullWidth type="submit" variant="contained" size="large" disabled={submitting}>
          {submitting ? <CircularProgress size={22} color="inherit" /> : 'Activate Account'}
        </Button>
      </form>
    </AuthLayout>
  );
}
